import { BaseApiClient } from './base-client';

export interface VPNUser {
  username: string;
  full_name?: string;
  email?: string;
  enabled: boolean;
  online: boolean;
  last_seen?: string;
  traffic_used: number;
  traffic_limit: number;
  created_at: string;
  updated_at: string;
}

export interface VPNUserCreate {
  username: string;
  password: string;
  full_name?: string;
  email?: string;
  traffic_limit?: number;
  enabled?: boolean;
}

export interface VPNUserUpdate {
  password?: string;
  full_name?: string;
  email?: string;
  traffic_limit?: number;
  enabled?: boolean;
}

export interface VPNConfigDownload {
  url: string;
  filename: string;
  type: 'ovpn' | 'zip';
}

export interface VPNStatus {
  status: string;
  status_openvpn?: string;
  connected_users?: number;
  active_users?: number;
  max_users?: number;
  server_address?: string;
  server_name?: string;
  port?: number;
  server_port?: string;
  protocol: string;
  cipher?: string;
  uptime?: string;
  users?: VPNUser[];
  vpn_users?: VPNUser[];
}

export interface VPNSession {
  username: string;
  real_address: string;
  virtual_address: string;
  bytes_sent: number;
  bytes_received: number;
  connected_since: string;
  last_activity: string;
}

export interface VPNTrafficStats {
  total_traffic_in: number;
  total_traffic_out: number;
  current_connections: number;
  peak_connections: number;
  uptime_seconds: number;
}

export class VPNApiClient extends BaseApiClient {
  // Status endpoints
  async getStatus(serviceId: number): Promise<VPNStatus> {
    return this.get<VPNStatus>(`/api/v1/vpn/${serviceId}/status`);
  }

  async getSessions(serviceId: number): Promise<VPNSession[]> {
    return this.get<VPNSession[]>(`/api/v1/vpn/${serviceId}/sessions`);
  }

  async getTrafficStats(serviceId: number): Promise<VPNTrafficStats> {
    return this.get<VPNTrafficStats>(`/api/v1/vpn/${serviceId}/traffic`);
  }

  // User management
  async listUsers(serviceId: number): Promise<VPNUser[]> {
    return this.get<VPNUser[]>(`/api/v1/vpn/${serviceId}/users`);
  }

  async getUser(serviceId: number, username: string): Promise<VPNUser> {
    return this.get<VPNUser>(`/api/v1/vpn/${serviceId}/users/${username}`);
  }

  async createUser(serviceId: number, user: VPNUserCreate): Promise<VPNUser> {
    return this.post<VPNUser>(`/api/v1/vpn/${serviceId}/users`, user);
  }

  async updateUser(serviceId: number, username: string, updates: VPNUserUpdate): Promise<VPNUser> {
    return this.patch<VPNUser>(`/api/v1/vpn/${serviceId}/users/${username}`, updates);
  }

  async deleteUser(serviceId: number, username: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/vpn/${serviceId}/users/${username}`);
  }

  async resetUserPassword(serviceId: number, username: string, newPassword: string): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/vpn/${serviceId}/users/${username}/reset-password`, {
      password: newPassword
    });
  }

  async toggleUserStatus(serviceId: number, username: string, enabled: boolean): Promise<VPNUser> {
    return this.patch<VPNUser>(`/api/v1/vpn/${serviceId}/users/${username}`, { enabled });
  }

  // Connection management
  async disconnectUser(serviceId: number, username: string): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/vpn/${serviceId}/users/${username}/disconnect`);
  }

  async revokeUser(serviceId: number, username: string): Promise<{ status: string; message: string }> {
    return this.post<{ status: string; message: string }>(`/api/v1/vpn/${serviceId}/users/${username}/revoke`);
  }

  async killAllSessions(serviceId: number): Promise<{ status: string; disconnected_count: number }> {
    return this.post<{ status: string; disconnected_count: number }>(`/api/v1/vpn/${serviceId}/sessions/kill-all`);
  }

  // Configuration download
  async getConfigDownloadUrl(serviceId: number, username: string, type: 'ovpn' | 'zip' = 'ovpn'): Promise<VPNConfigDownload> {
    return this.get<VPNConfigDownload>(`/api/v1/vpn/${serviceId}/users/${username}/config`, {
      params: { type }
    });
  }

  async downloadUserConfig(serviceId: number, username: string): Promise<Blob> {
    const response = await this.axios.get(`/api/v1/vpn/${serviceId}/users/${username}/config`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getActiveSessions(serviceId: number): Promise<any[]> {
    return this.get<any[]>(`/api/v1/vpn/${serviceId}/sessions`);
  }

  async downloadConfig(serviceId: number, username: string, type: 'ovpn' | 'zip' = 'ovpn'): Promise<Blob> {
    const response = await this.axios.get(`/api/v1/vpn/${serviceId}/users/${username}/config/download`, {
      params: { type },
      responseType: 'blob'
    });
    return response.data;
  }

  // Service control
  async restartService(serviceId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/vpn/${serviceId}/restart`);
  }

  async reloadService(serviceId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/vpn/${serviceId}/reload`);
  }
}